import { Component, Inject, OnDestroy, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { Router } from '@angular/router';
import { TranslocoService } from '@ngneat/transloco';

import {
  SMART_SEARCH_BAD_KEYWORDS,
  SMART_SEARCH_JOB_KEYWORDS
} from '../../../../services/smart-search-keywords';

interface SmartSearchForm {
  name: string;
  phone: string;
  email: string;
  title: string;
}

interface DetectedCvData extends SmartSearchForm {}

@Component({
  selector: 'app-smartsearch',
  templateUrl: './smartsearch.component.html',
  styleUrl: './smartsearch.component.scss'
})
export class SmartsearchComponent implements OnDestroy {
  selectedFile: File | null = null;
  dragOver = false;

  isUploading = false;
  progress = 0;

  currentStep: 'upload' | 'review' = 'upload';
  isCvAnalyzed = false;

  form: SmartSearchForm = {
    name: '',
    phone: '',
    email: '',
    title: ''
  };

  detectedTitle = '';
  detectedName = '';
  detectedPhone = '';
  detectedEmail = '';

  errorMessage = '';
  successMessage = '';

  private progressInterval: any = null;
  private readonly maxFileSize = 5 * 1024 * 1024;
  private readonly isBrowser: boolean;

  private readonly extraJobKeywords = [
    'orthodontist',
    'specialist orthodontist',
    'orthodontic specialist',
    'orthodontics specialist',
    'dentist',
    'dental surgeon',
    'general practitioner',
    'pediatric dentist',
    'pediatric dentistry',
    'aesthetic dermatologist',
    'dermatologist',
    'dermatology',
    'laser specialist',
    'aesthetic doctor',
    'skin doctor',

    'أخصائي تقويم أسنان',
    'اخصائي تقويم اسنان',
    'تقويم أسنان',
    'تقويم اسنان',
    'طبيب أسنان',
    'طبيب اسنان',
    'جراح أسنان',
    'جراح اسنان',
    'طبيب جلدية',
    'اخصائي جلدية',
    'أخصائي جلدية',
    'استشاري جلدية',
    'جلدية وتناسلية',
    'جلدية'
  ];

  private readonly extraBadKeywords = [
    'headline',
    'headlines',
    'professional summary',
    'personal details',
    'الملخص',
    'نبذة',
    'البيانات الشخصية'
  ];

  private readonly jobKeywords = [
    ...SMART_SEARCH_JOB_KEYWORDS,
    ...this.extraJobKeywords
  ];

  private readonly badKeywords = [
    ...SMART_SEARCH_BAD_KEYWORDS,
    ...this.extraBadKeywords
  ];

  constructor(
    private router: Router,
    @Inject(PLATFORM_ID) private platformId: Object,
    private transloco: TranslocoService
  ) {
    this.isBrowser = isPlatformBrowser(this.platformId);
  }

  ngOnDestroy(): void {
    this.clearProgress();
  }

  onFileChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];

    if (file) {
      this.setFile(file);
    }

    input.value = '';
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    this.dragOver = true;
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    this.dragOver = false;
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    this.dragOver = false;

    const file = event.dataTransfer?.files?.[0];

    if (file) {
      this.setFile(file);
    }
  }

  setFile(file: File): void {
    this.errorMessage = '';
    this.successMessage = '';
    this.currentStep = 'upload';
    this.isCvAnalyzed = false;
    this.resetDetectedData(true);

    const fileName = file.name.toLowerCase();

    const isAllowed =
      file.type === 'application/pdf' ||
      file.type === 'application/msword' ||
      file.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
      fileName.endsWith('.pdf') ||
      fileName.endsWith('.doc') ||
      fileName.endsWith('.docx');

    if (!isAllowed) {
      this.selectedFile = null;
      this.errorMessage = 'Please upload PDF, DOC, or DOCX file only.';
      return;
    }

    if (file.size > this.maxFileSize) {
      this.selectedFile = null;
      this.errorMessage = 'CV size must be less than 5MB.';
      return;
    }

    this.selectedFile = file;
  }

  removeFile(event: MouseEvent): void {
    event.stopPropagation();

    this.selectedFile = null;
    this.progress = 0;
    this.isUploading = false;
    this.errorMessage = '';
    this.successMessage = '';

    this.currentStep = 'upload';
    this.isCvAnalyzed = false;
    this.resetDetectedData(true);
    this.clearProgress();
  }

  async submitCv(event: Event): Promise<void> {
    event.preventDefault();

    if (!this.isCvAnalyzed) {
      await this.analyzeCvAndFillForm();
      return;
    }

    await this.submitSearchAfterReview();
  }

  private async analyzeCvAndFillForm(): Promise<void> {
    this.errorMessage = '';
    this.successMessage = '';
    this.resetDetectedData(false);

    if (!this.selectedFile) {
      this.errorMessage = 'Please upload your CV first.';
      return;
    }

    const file = this.selectedFile;

    try {
      this.isUploading = true;
      this.progress = 0;
      this.startProgress();

      const cvText = await this.extractTextFromFile(file);
      const detectedData = this.extractCvData(cvText);

      this.applyDetectedDataToForm(detectedData);

      this.currentStep = 'review';
      this.isCvAnalyzed = true;
      this.progress = 100;
      this.clearProgress();

      if (!detectedData.title) {
        this.errorMessage =
          'CV data was detected, but no clear job title was found. Please write the job title manually before searching.';
      } else {
        this.successMessage = this.transloco.translate('atw.smartSearch.cvDetectedSuccess');
      }
    } catch (error) {
      console.error('CV reading error:', error);

      const errorText = error instanceof Error ? error.message : String(error);

      if (errorText.includes('SCANNED_PDF_NO_TEXT_LAYER')) {
        this.errorMessage =
          'This PDF looks like a scanned image. Please upload a searchable PDF, DOC, or DOCX file.';
      } else {
        this.errorMessage =
          'Could not read this CV. Please upload a clear PDF, DOC, or DOCX file.';
      }
    } finally {
      this.stopLoading();
    }
  }

  private async submitSearchAfterReview(): Promise<void> {
    this.errorMessage = '';
    this.successMessage = '';

    const title = this.form.title.trim();

    if (!title) {
      this.errorMessage = 'Please add or confirm the job title before searching.';
      return;
    }

    this.detectedTitle = title;
    this.saveApplicantDataForJobsPage();

    await this.router.navigate(['/jobs'], {
      queryParams: {
        search: title
      }
    });
  }

  formatFileSize(bytes: number): string {
    if (!bytes) {
      return '0 KB';
    }

    const kb = bytes / 1024;

    if (kb < 1024) {
      return `${kb.toFixed(1)} KB`;
    }

    return `${(kb / 1024).toFixed(1)} MB`;
  }

  getFileTypeLabel(file: File | null): string {
    if (!file) {
      return 'CV';
    }

    const name = file.name.toLowerCase();

    if (name.endsWith('.pdf')) {
      return 'PDF';
    }

    if (name.endsWith('.docx')) {
      return 'DOCX';
    }

    if (name.endsWith('.doc')) {
      return 'DOC';
    }

    return 'CV';
  }

  private async extractTextFromFile(file: File): Promise<string> {
    const fileName = file.name.toLowerCase();

    if (file.type === 'application/pdf' || fileName.endsWith('.pdf')) {
      return this.extractPdfText(file);
    }

    if (
      file.type ===
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
      fileName.endsWith('.docx')
    ) {
      return this.extractDocxText(file);
    }

    if (file.type === 'application/msword' || fileName.endsWith('.doc')) {
      return this.extractLegacyDocText(file);
    }

      throw new Error('Unsupported file type');
  }

  private async extractPdfText(file: File): Promise<string> {
    if (!this.isBrowser) {
      throw new Error('PDF reading is available in browser only.');
    }

    const pdfjsLib: any = await import('pdfjs-dist/legacy/build/pdf.mjs');

    pdfjsLib.GlobalWorkerOptions.workerSrc = new URL(
      'pdfjs-dist/legacy/build/pdf.worker.mjs',
      import.meta.url
    ).toString();

    const arrayBuffer = await file.arrayBuffer();

    const loadingTask = pdfjsLib.getDocument({
      data: new Uint8Array(arrayBuffer)
    });

    const pdf = await loadingTask.promise;
    const maxPages = Math.min(pdf.numPages || 1, 5);

    let text = '';

    for (let pageNumber = 1; pageNumber <= maxPages; pageNumber++) {
      const page = await pdf.getPage(pageNumber);
      const content = await page.getTextContent();

      text += this.pdfItemsToLines(content.items || []);
      text += '\n';
    }

    if (pdf?.destroy) {
      pdf.destroy();
    }

    const cleanText = text.replace(/\s+/g, ' ').trim();

    if (cleanText.length < 30) {
      throw new Error('SCANNED_PDF_NO_TEXT_LAYER');
    }

    return text;
  }

  private pdfItemsToLines(items: any[]): string {
    const rows = new Map<number, Array<{ x: number; text: string }>>();

    items.forEach((item: any) => {
      const value = String(item?.str || '').trim();

      if (!value) {
        return;
      }

      const transform = item?.transform || [];
      const x = Math.round(Number(transform[4] || 0));
      const y = Math.round(Number(transform[5] || 0));

      if (!rows.has(y)) {
        rows.set(y, []);
      }

      rows.get(y)?.push({
        x,
        text: value
      });
    });

    return Array.from(rows.entries())
      .sort((a, b) => b[0] - a[0])
      .map(([, rowItems]) => {
        return rowItems
          .sort((a, b) => a.x - b.x)
          .map(item => item.text)
          .join(' ');
      })
      .join('\n');
  }

private async extractDocxText(file: File): Promise<string> {
  if (!this.isBrowser) {
    throw new Error('DOCX reading is available in browser only.');
  }

  const mammothModule: any = await import('mammoth');

  const mammoth =
    mammothModule?.extractRawText
      ? mammothModule
      : mammothModule?.default?.extractRawText
        ? mammothModule.default
        : mammothModule?.default?.default?.extractRawText
          ? mammothModule.default.default
          : null;

  if (!mammoth || typeof mammoth.extractRawText !== 'function') {
    throw new Error('DOCX_READER_NOT_READY');
  }

  const arrayBuffer = await file.arrayBuffer();

  const result = await mammoth.extractRawText({
    arrayBuffer
  });

  return String(result?.value || '').trim();
}

  /**
   * Legacy .doc files are a binary format. We recover their readable text without
   * sending the CV anywhere; modern .docx files still use Mammoth above.
   */
  private async extractLegacyDocText(file: File): Promise<string> {
    if (!this.isBrowser) {
      throw new Error('DOC reading is available in browser only.');
    }

    const bytes = new Uint8Array(await file.arrayBuffer());
    const text = new TextDecoder('windows-1252')
      .decode(bytes)
      .replace(/[\x00-\x08\x0B\x0C\x0E-\x1F]+/g, '\n')
      .replace(/[^\S\r\n]+/g, ' ')
      .replace(/\n{2,}/g, '\n')
      .trim();

    if (text.length < 30) {
      throw new Error('LEGACY_DOC_NO_TEXT');
    }

    return text;
  }

  private extractCvData(text: string): DetectedCvData {
    const cleanText = this.prepareCvText(text);

    return {
      name: this.extractFullName(cleanText),
      phone: this.extractPhone(cleanText),
      email: this.extractEmail(cleanText),
      title: this.extractJobTitle(cleanText)
    };
  }

  private applyDetectedDataToForm(data: DetectedCvData): void {
    this.detectedName = data.name || '';
    this.detectedPhone = data.phone || '';
    this.detectedEmail = data.email || '';
    this.detectedTitle = data.title || '';

    this.form = {
      name: this.form.name?.trim() || data.name || '',
      phone: this.form.phone?.trim() || data.phone || '',
      email: this.form.email?.trim() || data.email || '',
      title: this.form.title?.trim() || data.title || ''
    };
  }

  resetDetectedData(resetForm: boolean): void {
    this.detectedTitle = '';
    this.detectedName = '';
    this.detectedPhone = '';
    this.detectedEmail = '';

    if (resetForm) {
      this.form = {
        name: '',
        phone: '',
        email: '',
        title: ''
      };
    }
  }

  private saveApplicantDataForJobsPage(): void {
    if (!this.isBrowser) {
      return;
    }

    const applicantData = {
      name: this.form.name,
      phone: this.form.phone,
      email: this.form.email,
      title: this.form.title.trim() || this.detectedTitle,
      cvFileName: this.selectedFile?.name || ''
    };

    sessionStorage.setItem(
      'smartSearchApplicantData',
      JSON.stringify(applicantData)
    );
  }

  private extractEmail(text: string): string {
    const normalizedText = this.prepareCvText(text)
      .replace(/\s*\[at\]\s*/gi, '@')
      .replace(/\s*\(at\)\s*/gi, '@')
      .replace(/\s+@\s+/g, '@')
      .replace(/\s*\[dot\]\s*/gi, '.')
      .replace(/\s*\(dot\)\s*/gi, '.');

    const emails =
      normalizedText.match(/[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/gi) || [];

    return emails[0] || '';
  }

  private extractPhone(text: string): string {
    const textWithoutEmails = this.prepareCvText(text).replace(
      /[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/gi,
      ' '
    );

    const matches = Array.from(
      textWithoutEmails.matchAll(/(?:\+|00)?\d[\d\s().-]{7,}\d/g)
    );

    const phones = matches
      .map(match => ({
        phone: this.normalizePhone(match[0]),
        score: this.scorePhone(match[0], textWithoutEmails, match.index ?? -1)
      }))
      .filter(item => this.isValidPhone(item.phone));

    const uniquePhones = Array.from(
      new Map(phones.map(item => [item.phone, item])).values()
    );

    uniquePhones.sort((a, b) => b.score - a.score);

    return uniquePhones[0]?.phone || '';
  }

  private normalizePhone(value: string): string {
    let phone = String(value || '').trim();

    phone = phone.replace(/[^\d+]/g, '');

    if (phone.startsWith('00')) {
      phone = `+${phone.slice(2)}`;
    }

    const digits = phone.replace(/\D/g, '');

    if (/^20(10|11|12|15)\d{8}$/.test(digits)) {
      return `+${digits}`;
    }

    if (/^(10|11|12|15)\d{8}$/.test(digits)) {
      return `0${digits}`;
    }

    if (/^0(10|11|12|15)\d{8}$/.test(digits)) {
      return digits;
    }

    if (phone.startsWith('+') && digits.length >= 8 && digits.length <= 15) {
      return `+${digits}`;
    }

    return digits;
  }

  private isValidPhone(phone: string): boolean {
    const digits = String(phone || '').replace(/\D/g, '');

    if (digits.length < 8 || digits.length > 15) {
      return false;
    }

    if (/^(\d)\1+$/.test(digits)) {
      return false;
    }

    if (digits.length === 14 && /^[23]/.test(digits)) {
      return false;
    }

    return true;
  }

  private scorePhone(value: string, sourceText = '', matchIndex = -1): number {
    const phone = this.normalizePhone(value);
    const digits = phone.replace(/\D/g, '');

    let score = 0;

    if (/^0(10|11|12|15)\d{8}$/.test(digits)) {
      score += 80;
    }

    if (/^20(10|11|12|15)\d{8}$/.test(digits)) {
      score += 90;
    }

    if (phone.startsWith('+')) {
      score += 20;
    }

    if (digits.length >= 10 && digits.length <= 13) {
      score += 15;
    }

    if (matchIndex >= 0) {
      const nearbyText = sourceText.slice(Math.max(0, matchIndex - 35), matchIndex);
      if (/(mobile|phone|tel(?:ephone)?|whatsapp|contact|الهاتف|الموبايل|الجوال|تليفون)/i.test(nearbyText)) {
        score += 60;
      }
    }

    return score;
  }

  private extractFullName(text: string): string {
    const lines = this.getCleanLines(text);

    const nameFromLabel = this.findNameFromLabels(lines);

    if (nameFromLabel) {
      return nameFromLabel;
    }

    const candidates: Array<{ name: string; score: number; index: number }> = [];

    lines.slice(0, 45).forEach((line, index) => {
      const name = this.cleanPersonName(line);

      if (!this.isLikelyPersonName(name)) {
        return;
      }

      let score = 100 - index;

      if (index <= 5) {
        score += 35;
      } else if (index <= 12) {
        score += 20;
      }

      if (this.looksLikeRealPersonName(name)) {
        score += 25;
      }

      candidates.push({
        name,
        score,
        index
      });
    });

    candidates.sort((a, b) => {
      if (b.score !== a.score) {
        return b.score - a.score;
      }

      return a.index - b.index;
    });

    return candidates[0]?.name || '';
  }

  private findNameFromLabels(lines: string[]): string {
    const labelWithValuePatterns = [
      /(?:full\s*name|candidate\s*name|applicant\s*name|name)\s*[:\-–—|]\s*(.{2,80})/i,
      /(?:الاسم\s*الكامل|اسم\s*المتقدم|اسم\s*المرشح|الاسم)\s*[:\-–—|]\s*(.{2,80})/i
    ];

    const labelOnlyPattern =
      /^(full\s*name|candidate\s*name|applicant\s*name|name|الاسم\s*الكامل|اسم\s*المتقدم|اسم\s*المرشح|الاسم)\s*[:\-–—|]?$/i;

    for (let index = 0; index < Math.min(lines.length, 80); index++) {
      const line = lines[index];

      for (const pattern of labelWithValuePatterns) {
        const match = line.match(pattern);

        if (match?.[1]) {
          const name = this.cleanPersonName(match[1]);

          if (this.isLikelyPersonName(name)) {
            return name;
          }
        }
      }

      if (labelOnlyPattern.test(line)) {
        const nextLines = lines.slice(index + 1, index + 4);

        for (const nextLine of nextLines) {
          const name = this.cleanPersonName(nextLine);

          if (this.isLikelyPersonName(name)) {
            return name;
          }
        }
      }
    }

    return '';
  }

  private cleanPersonName(value: string): string {
    return String(value || '')
      .replace(
        /^(full\s*name|candidate\s*name|applicant\s*name|name|الاسم\s*الكامل|اسم\s*المتقدم|اسم\s*المرشح|الاسم)\s*[:\-–—|]?\s*/i,
        ''
      )
      .replace(/\b(curriculum\s*vitae|resume|cv)\b/gi, '')
      .replace(/[●•▪▫■□◆◇▶►]/g, ' ')
      .replace(/[()[\]{}<>]/g, '')
      .replace(/\s+/g, ' ')
      .replace(/^[-–—:|.,،;]+|[-–—:|.,،;]+$/g, '')
      .trim();
  }

  private isLikelyPersonName(value: string): boolean {
    const name = this.cleanPersonName(value);

    if (!name) {
      return false;
    }

    if (this.looksLikeContactOrMeta(name)) {
      return false;
    }

    if (this.hasJobKeyword(name)) {
      return false;
    }

    if (this.isBadKeywordLine(name)) {
      return false;
    }

    if (this.isSectionHeading(name)) {
      return false;
    }

    if (this.looksLikeDateLine(name)) {
      return false;
    }

    if (this.looksLikeEducationDegree(name)) {
      return false;
    }

    if (/\d/.test(name)) {
      return false;
    }

    if (/https?:\/\/|www\.|linkedin|github|behance/i.test(name)) {
      return false;
    }

    if (/(email|phone|mobile|address|location|objective|summary|education|experience|skills|courses|certificates|references|profile|contact|الايميل|الإيميل|الهاتف|الموبايل|العنوان|التعليم|الخبرة|المهارات|الدورات|الشهادات)/i.test(name)) {
      return false;
    }

    return this.looksLikeRealPersonName(name);
  }

  private looksLikeRealPersonName(value: string): boolean {
    const name = this.cleanPersonName(value);
    const words = name.split(/\s+/).filter(Boolean);

    if (words.length < 2 || words.length > 5) {
      return false;
    }

    const englishName =
      /^[A-Za-z\s.'-]+$/.test(name) &&
      words.every(word => /^[A-Za-z][A-Za-z.'-]{1,}$/.test(word));

    const arabicName =
      /^[\u0600-\u06FF\s.'-]+$/.test(name) &&
      words.every(word => /^[\u0600-\u06FF]{2,}$/.test(word));

    return englishName || arabicName;
  }

  private extractJobTitle(text: string): string {
    const lines = this.getCleanLines(text);

    if (!lines.length) {
      return '';
    }

    const candidates: Array<{
      title: string;
      score: number;
      source: string;
      index: number;
    }> = [];

    const addCandidate = (
      value: string,
      score: number,
      source: string,
      index: number
    ): void => {
      const title = this.cleanTitle(value);

      if (!this.isValidTitle(title)) {
        return;
      }

      candidates.push({
        title,
        score,
        source,
        index
      });
    };

    const labeledTitle = this.findTitleFromLabels(lines);
    if (labeledTitle) {
      return labeledTitle;
    }

    const latestExperienceTitle = this.findLatestExperienceTitle(lines);
    if (latestExperienceTitle) {
      return latestExperienceTitle;
    }

    const summaryTitle = this.findTitleFromSummary(lines);
    if (summaryTitle) {
      return summaryTitle;
    }

    lines.slice(0, 120).forEach((line, index) => {
      const bestSegment = this.getBestSegment(line);
      const score = this.scoreTitleCandidate(bestSegment, index);

      addCandidate(bestSegment, score, 'top-lines', index);
    });

    const uniqueCandidates = this.removeDuplicateTitleCandidates(candidates)
      .filter(item => item.score >= 55)
      .sort((a, b) => {
        if (b.score !== a.score) {
          return b.score - a.score;
        }

        return a.index - b.index;
      });

    if (uniqueCandidates[0]?.title) {
      return uniqueCandidates[0].title;
    }

    return this.findKeywordFallback(lines);
  }

  private getCleanLines(text: string): string[] {
    return this.prepareCvText(text)
      .replace(/\r/g, '\n')
      .replace(/\t/g, ' ')
      .split('\n')
      .map(line => {
        return line
          .replace(/[●•▪▫■□◆◇▶►]/g, ' ')
          .replace(/\s+/g, ' ')
          .trim();
      })
      .filter(line => line.length >= 2 && line.length <= 140);
  }

  /** Normalise common CV copy/paste issues before matching Arabic or English fields. */
  private prepareCvText(value: string): string {
    const text = String(value || '')
      .replace(/[٠-٩]/g, digit => String('٠١٢٣٤٥٦٧٨٩'.indexOf(digit)))
      .replace(/[۰-۹]/g, digit => String('۰۱۲۳۴۵۶۷۸۹'.indexOf(digit)));

    // Some old PDFs expose UTF-8 Arabic as mojibake (Ø§Ù„...). Repair it
    // only when that signature is present, leaving normal Arabic untouched.
    if (!/[ØÙ]/.test(text)) {
      return text;
    }

    try {
      const bytes = Uint8Array.from(text, char => char.charCodeAt(0) & 0xff);
      const repaired = new TextDecoder('utf-8').decode(bytes);
      return /[\u0600-\u06FF]/.test(repaired) ? repaired : text;
    } catch {
      return text;
    }
  }

  private findTitleFromLabels(lines: string[]): string {
    const labelWithValuePatterns = [
      /(?:job\s*title|current\s*position|current\s*job|position|profession|role|designation|occupation|career\s*target|target\s*job|desired\s*position|target\s*position|applied\s*position)\s*[:\-–—|]\s*(.{2,90})/i,
      /(?:المسمى\s*الوظيفي|الوظيفة\s*الحالية|الوظيفة|المنصب|المهنة|الدور|الوظيفة\s*المستهدفة|المنصب\s*المستهدف|الوظيفة\s*المطلوبة)\s*[:\-–—|]\s*(.{2,90})/i
    ];

    const labelOnlyPattern =
      /^(job\s*title|current\s*position|current\s*job|position|profession|role|designation|occupation|career\s*target|target\s*job|desired\s*position|target\s*position|applied\s*position|المسمى\s*الوظيفي|الوظيفة\s*الحالية|الوظيفة|المنصب|المهنة|الدور|الوظيفة\s*المستهدفة|المنصب\s*المستهدف|الوظيفة\s*المطلوبة)\s*[:\-–—|]?$/i;

    for (let index = 0; index < Math.min(lines.length, 100); index++) {
      const line = lines[index];

      for (const pattern of labelWithValuePatterns) {
        const match = line.match(pattern);

        if (match?.[1]) {
          const title = this.cleanTitle(match[1]);

          if (this.isValidTitle(title)) {
            return title;
          }
        }
      }

      if (labelOnlyPattern.test(line)) {
        const nextLines = lines.slice(index + 1, index + 4);

        for (const nextLine of nextLines) {
          const title = this.cleanTitle(nextLine);

          if (this.isValidTitle(title) && this.hasJobKeyword(title)) {
            return title;
          }
        }
      }
    }

    return '';
  }

  private findTitleFromSummary(lines: string[]): string {
    const topText = lines.slice(0, 80).join(' ');

    const patterns = [
      /(?:working|work|worked)\s+as\s+(?:a|an)?\s*([^,.|;]{2,80})/i,
      /(?:experience|experienced)\s+(?:as\s+)?(?:a|an)?\s*([^,.|;]{2,80}?)(?:\s+with|\s+for|\s+in|,|\.|$)/i,
      /(?:seeking|looking\s+for)\s+(?:a|an)?\s*(?:position|role|job)?\s*(?:as\s+)?([^,.|;]{2,80})/i,
      /(?:أعمل|اعمل|عملت)\s+(?:ك|في وظيفة)\s*([^،.;|]{2,80})/i,
      /(?:خبرة|لدي خبرة|عندي خبرة)\s+(?:ك|في مجال|في وظيفة)?\s*([^،.;|]{2,80})/i,
      /(?:أبحث|ابحث)\s+عن\s+(?:وظيفة|فرصة)?\s*([^،.;|]{2,80})/i
    ];

    for (const pattern of patterns) {
      const match = topText.match(pattern);

      if (!match?.[1]) {
        continue;
      }

      const title = this.getBestSegment(match[1]);

      if (this.isValidTitle(title) && this.hasJobKeyword(title)) {
        return title;
      }
    }

    return '';
  }

  private findLatestExperienceTitle(lines: string[]): string {
    const experienceHeadingPattern =
      /^(work\s*history|work\s*experience|professional\s*experience|employment\s*history|career\s*history|experience|professional\s*background|career\s*summary|الخبرة|الخبرات|الخبرة\s*العملية|الخبرات\s*العملية|الخبرة\s*المهنية)$/i;

    const stopSectionPattern =
      /^(education|academic|qualification|qualifications|skills|courses|certifications|certificates|projects|languages|references|professional\s*summary|summary|profile|about\s*me|headlines|headline|contact|personal\s*details|التعليم|المؤهل|المؤهلات|المهارات|الدورات|الشهادات|المشاريع|اللغات|المراجع|الملخص|نبذة|البيانات\s*الشخصية)$/i;

    const experienceIndex = lines.findIndex(line =>
      experienceHeadingPattern.test(this.normalizeText(line))
    );

    if (experienceIndex === -1) {
      return '';
    }

    const candidates: Array<{ title: string; score: number; index: number }> = [];

    for (
      let index = experienceIndex + 1;
      index < Math.min(lines.length, experienceIndex + 70);
      index++
    ) {
      const rawLine = lines[index];
      const normalizedLine = this.normalizeText(rawLine);

      if (stopSectionPattern.test(normalizedLine)) {
        break;
      }

      if (
        this.isSectionHeading(rawLine) ||
        this.looksLikeContactOrMeta(rawLine) ||
        this.looksLikeEducationDegree(rawLine)
      ) {
        continue;
      }

      const lineWithoutDates = this.stripExperienceDates(rawLine);
      const title = this.getBestSegment(lineWithoutDates);
      const score =
        this.scoreTitleCandidate(title, index) +
        40 +
        this.getExperienceLineBonus(rawLine, title);

      if (this.isValidTitle(title) && this.hasJobKeyword(title)) {
        candidates.push({ title, score, index });
      }
    }

    candidates.sort((a, b) => {
      if (b.score !== a.score) {
        return b.score - a.score;
      }

      return a.index - b.index;
    });

    return candidates[0]?.title || '';
  }

  private stripExperienceDates(value: string): string {
    return String(value || '')
      .replace(
        /\b(jan|feb|mar|apr|may|jun|jul|aug|sep|sept|oct|nov|dec|january|february|march|april|june|july|august|september|october|november|december)\b.*$/i,
        ''
      )
      .replace(/\b(19|20)\d{2}\b.*$/i, '')
      .replace(/\b(present|current|currently|now)\b.*$/i, '')
      .replace(/\b(يناير|فبراير|مارس|ابريل|أبريل|مايو|يونيو|يوليو|اغسطس|أغسطس|سبتمبر|اكتوبر|أكتوبر|نوفمبر|ديسمبر|حاليا|حاليًا|حتى الان|حتى الآن)\b.*$/i, '')
      .replace(/\s*[-–—]\s*$/g, '')
      .replace(/\s+/g, ' ')
      .trim();
  }

  private getExperienceLineBonus(rawLine: string, title: string): number {
    let bonus = 0;

    const line = String(rawLine || '');

    if (/\b(present|current|currently|now)\b/i.test(line)) {
      bonus += 12;
    }

    if (/\b(19|20)\d{2}\b/.test(line)) {
      bonus += 8;
    }

    if (/\b(specialist|consultant|orthodontist|dermatologist|surgeon|dentist|pharmacist|engineer|manager)\b/i.test(title)) {
      bonus += 15;
    }

    if (/(اخصائي|أخصائي|إخصائي|استشاري|طبيب|دكتور|مهندس|مدير)/.test(title)) {
      bonus += 15;
    }

    if (/\bgeneral\s+practitioner\b/i.test(title)) {
      bonus -= 8;
    }

    const years = line.match(/\b(19|20)\d{2}\b/g) || [];

    if (years.length) {
      const latestYear = Math.max(...years.map(year => Number(year)));
      bonus += Math.max(0, Math.min(10, latestYear - 2015));
    }

    return bonus;
  }

  private getBestSegment(line: string): string {
    const cleanedLine = this.cleanTitle(line);

    const segments = cleanedLine
      .split(/\s+(?:at|@|in)\s+|[|،,•·;]| - | – | — /i)
      .map(segment => this.cleanTitle(segment))
      .filter(Boolean);

    const segmentWithStrongKeyword = segments
      .filter(segment => this.hasJobKeyword(segment))
      .sort((a, b) => {
        const aKeyword = this.findStrongestJobKeyword(a);
        const bKeyword = this.findStrongestJobKeyword(b);

        return bKeyword.length - aKeyword.length;
      })[0];

    if (segmentWithStrongKeyword) {
      return segmentWithStrongKeyword;
    }

    const extractedKeywordTitle = this.extractKeywordTitleFromSentence(cleanedLine);

    return extractedKeywordTitle || cleanedLine;
  }

  private extractKeywordTitleFromSentence(line: string): string {
    const strongestKeyword = this.findStrongestJobKeyword(line);

    if (!strongestKeyword) {
      return '';
    }

    const words = line.split(/\s+/).filter(Boolean);

    for (let start = 0; start < words.length; start++) {
      for (let end = start + 1; end <= Math.min(words.length, start + 8); end++) {
        const part = words.slice(start, end).join(' ');

        if (
          this.hasJobKeyword(part) &&
          this.isValidTitle(part) &&
          this.countWords(part) <= 8
        ) {
          return this.cleanTitle(part);
        }
      }
    }

    return this.cleanTitle(strongestKeyword);
  }

  private scoreTitleCandidate(line: string, index: number): number {
    const title = this.cleanTitle(line);

    if (!this.isValidTitle(title)) {
      return -100;
    }

    let score = 0;

    const strongestKeyword = this.findStrongestJobKeyword(title);

    if (strongestKeyword) {
      score += this.isGenericJobKeyword(strongestKeyword) ? 25 : 55;
    } else {
      score -= 35;
    }

    if (index <= 6) {
      score += 35;
    } else if (index <= 20) {
      score += 22;
    } else if (index <= 50) {
      score += 10;
    }

    const wordsCount = this.countWords(title);

    if (wordsCount >= 2 && wordsCount <= 6) {
      score += 20;
    } else if (wordsCount === 1 && this.hasJobKeyword(title)) {
      score += 8;
    } else if (wordsCount > 8) {
      score -= 20;
    }

    if (this.looksLikeTitleCase(title) || this.looksLikeArabicJobTitle(title)) {
      score += 10;
    }

    if (this.looksLikeSentence(title)) {
      score -= 35;
    }

    if (this.looksLikeDateLine(title)) {
      score -= 50;
    }

    if (this.looksLikeEducationDegree(title)) {
      score -= 100;
    }

    if (this.looksLikePersonName(title)) {
      score -= 45;
    }

    if (this.looksLikeContactOrMeta(title)) {
      score -= 70;
    }

    return score;
  }

  private isValidTitle(value: string): boolean {
    const title = this.cleanTitle(value);

    if (!title) {
      return false;
    }

    if (this.isSectionHeading(title)) {
      return false;
    }

    if (this.looksLikeEducationDegree(title)) {
      return false;
    }

    if (title.length < 3 || title.length > 90) {
      return false;
    }

    if (!/[A-Za-z\u0600-\u06FF]/.test(title)) {
      return false;
    }

    if (this.countWords(title) > 10) {
      return false;
    }

    if (this.looksLikeContactOrMeta(title)) {
      return false;
    }

    if (this.looksLikeDateLine(title)) {
      return false;
    }

    if (this.isBadKeywordLine(title)) {
      return false;
    }

    if (this.looksLikeSentence(title) && !this.hasJobKeyword(title)) {
      return false;
    }

    return true;
  }

  private cleanTitle(value: string): string {
    return String(value || '')
      .replace(
        /^(job\s*title|current\s*position|current\s*job|position|profession|role|designation|occupation|career\s*target|target\s*job|desired\s*position|target\s*position|applied\s*position)\s*[:\-–—|]?\s*/i,
        ''
      )
      .replace(
        /^(المسمى\s*الوظيفي|الوظيفة\s*الحالية|الوظيفة|المنصب|المهنة|الدور|الوظيفة\s*المستهدفة|المنصب\s*المستهدف|الوظيفة\s*المطلوبة)\s*[:\-–—|]?\s*/i,
        ''
      )
      .replace(/\s+/g, ' ')
      .replace(/[()[\]{}<>]/g, '')
      .replace(/^[-–—:|.,،;]+|[-–—:|.,،;]+$/g, '')
      .trim();
  }

  private isSectionHeading(value: string): boolean {
    const normalizedValue = this.normalizeText(value);

    const headings = [
      'personal details',
      'contact',
      'skills',
      'professional summary',
      'summary',
      'profile',
      'about me',
      'headlines',
      'headline',
      'work history',
      'work experience',
      'professional experience',
      'employment history',
      'education',
      'courses',
      'certifications',
      'certificates',
      'languages',
      'references',

      'البيانات الشخصية',
      'بيانات شخصية',
      'التواصل',
      'المهارات',
      'الملخص',
      'نبذة',
      'الخبرة',
      'الخبرات',
      'الخبرة العملية',
      'التعليم',
      'الدورات',
      'الشهادات',
      'اللغات',
      'المراجع'
    ];

    return headings.some(item => this.normalizeText(item) === normalizedValue);
  }

  private looksLikeEducationDegree(value: string): boolean {
    const title = String(value || '').trim();
    const normalizedTitle = this.normalizeText(title);

    if (!title) {
      return false;
    }

    return (
      /\b(master|bachelor|bds|mds|msc|m\.sc|phd|doctorate|diploma|degree|faculty|university|college|gpa|thesis|coursework|course|courses|graduation|graduate|education|academic)\b/i.test(title) ||
      /(ماجستير|بكالوريوس|دكتوراه|دبلوم|درجة|كليه|جامعه|رساله|تقدير|التعليم|المؤهل|مقررات|دورات|شهاده|شهادة)/.test(normalizedTitle)
    );
  }

  private hasJobKeyword(value: string): boolean {
    return !!this.findStrongestJobKeyword(value);
  }

  private findStrongestJobKeyword(value: string): string {
    const normalizedValue = this.normalizeText(value);

    if (!normalizedValue) {
      return '';
    }

    const sortedKeywords = [...this.jobKeywords]
      .map(keyword => String(keyword || '').trim())
      .filter(Boolean)
      .sort((a, b) => b.length - a.length);

    for (const keyword of sortedKeywords) {
      const normalizedKeyword = this.normalizeText(keyword);

      if (!normalizedKeyword) {
        continue;
      }

      if (this.keywordExistsInText(normalizedValue, normalizedKeyword)) {
        return keyword;
      }
    }

    return '';
  }

  private keywordExistsInText(text: string, keyword: string): boolean {
    const escapedKeyword = this.escapeRegex(keyword);

    const isEnglish = /^[a-z0-9 .+#/-]+$/i.test(keyword);

    if (isEnglish) {
      const pattern = new RegExp(`(^|[^a-z0-9])${escapedKeyword}([^a-z0-9]|$)`, 'i');
      return pattern.test(text);
    }

    return text.includes(keyword);
  }

  private isGenericJobKeyword(keyword: string): boolean {
    const genericKeywords = [
      'manager',
      'head',
      'lead',
      'senior',
      'junior',
      'specialist',
      'consultant',
      'officer',
      'executive',
      'assistant',
      'coordinator',
      'supervisor',
      'representative',
      'agent',
      'associate',
      'admin',
      'sales',
      'marketing',
      'finance',
      'quality',
      'production',
      'engineer',
      'developer',
      'designer',
      'doctor',
      'nurse',
      'teacher',
      'مدير',
      'رئيس',
      'مشرف',
      'مساعد',
      'منسق',
      'مسؤول',
      'اخصائي',
      'أخصائي',
      'إخصائي',
      'استشاري',
      'مندوب',
      'موظف',
      'تنفيذي',
      'مهندس',
      'مطور',
      'مصمم',
      'طبيب',
      'دكتور',
      'ممرض',
      'مدرس'
    ];

    const normalizedKeyword = this.normalizeText(keyword);

    return genericKeywords.some(item => this.normalizeText(item) === normalizedKeyword);
  }

  private isBadKeywordLine(value: string): boolean {
    const normalizedValue = this.normalizeText(value);

    if (!normalizedValue) {
      return true;
    }

    return this.badKeywords.some(keyword => {
      const normalizedKeyword = this.normalizeText(keyword);

      if (!normalizedKeyword) {
        return false;
      }

      if (normalizedValue === normalizedKeyword) {
        return true;
      }

      if (
        normalizedValue.startsWith(`${normalizedKeyword}:`) ||
        normalizedValue.startsWith(`${normalizedKeyword} -`) ||
        normalizedValue.startsWith(`${normalizedKeyword} |`)
      ) {
        return true;
      }

      if (normalizedKeyword.length <= 3) {
        return false;
      }

      const pattern = new RegExp(
        `(^|[^A-Za-z0-9\\u0600-\\u06FF])${this.escapeRegex(normalizedKeyword)}([^A-Za-z0-9\\u0600-\\u06FF]|$)`,
        'i'
      );

      return pattern.test(normalizedValue);
    });
  }

  private looksLikeContactOrMeta(value: string): boolean {
    const title = String(value || '');

    if (/\S+@\S+\.\S+/.test(title)) {
      return true;
    }

    if (/https?:\/\/|www\.|linkedin|github|behance/i.test(title)) {
      return true;
    }

    if (/\+?\d[\d\s\-()]{6,}/.test(title)) {
      return true;
    }

    if (/^\d+[\s\-.,/]*$/.test(title)) {
      return true;
    }

    return false;
  }

  private looksLikeDateLine(value: string): boolean {
    const title = String(value || '').trim();

    if (/\b(19|20)\d{2}\b/.test(title)) {
      return true;
    }

    if (/\b(jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec)\b/i.test(title)) {
      return true;
    }

    if (/\b(present|current|currently|now)\b/i.test(title)) {
      return true;
    }

    if (/\b(يناير|فبراير|مارس|ابريل|أبريل|مايو|يونيو|يوليو|اغسطس|أغسطس|سبتمبر|اكتوبر|أكتوبر|نوفمبر|ديسمبر|حاليا|حاليًا|حتى الان|حتى الآن)\b/i.test(title)) {
      return true;
    }

    return false;
  }

  private looksLikeSentence(value: string): boolean {
    const title = String(value || '').trim();

    if (this.countWords(title) >= 11) {
      return true;
    }

    return /^(i am|i'm|i have|seeking|looking|objective|responsible for|worked on|experienced in|about me|profile|انا|أنا|لدي|عندي|أبحث|ابحث|مسؤول عن)/i.test(title);
  }

  private looksLikePersonName(value: string): boolean {
    const title = String(value || '').trim();

    if (this.hasJobKeyword(title)) {
      return false;
    }

    if (/\d/.test(title)) {
      return false;
    }

    const words = title.split(/\s+/).filter(Boolean);

    if (words.length < 2 || words.length > 4) {
      return false;
    }

    const englishNameLike = words.every(word => /^[A-Z][a-z]{2,}$/.test(word));

    const arabicNameLike =
      /^[\u0600-\u06FF\s]+$/.test(title) &&
      !/(مدير|مهندس|محاسب|طبيب|دكتور|مندوب|مصمم|مطور|اخصائي|أخصائي|مشرف|منسق|مسؤول|مساعد|موظف)/.test(title);

    return englishNameLike || arabicNameLike;
  }

  private looksLikeTitleCase(value: string): boolean {
    return /[A-Z][a-z]+/.test(value) || /^[A-Z\s/&.-]{4,}$/.test(value);
  }

  private looksLikeArabicJobTitle(value: string): boolean {
    return /(مدير|مهندس|محاسب|طبيب|دكتور|صيدلي|ممرض|مندوب|مصمم|مطور|اخصائي|أخصائي|إخصائي|مشرف|منسق|مسؤول|مساعد|موظف|فني|مدرس|معلم)/.test(value);
  }

  private findKeywordFallback(lines: string[]): string {
    for (const line of lines.slice(0, 150)) {
      if (
        this.looksLikeContactOrMeta(line) ||
        this.isBadKeywordLine(line) ||
        this.isSectionHeading(line) ||
        this.looksLikeEducationDegree(line)
      ) {
        continue;
      }

      const keyword = this.findStrongestJobKeyword(line);

      if (keyword && !this.isGenericJobKeyword(keyword) && this.isValidTitle(keyword)) {
        return this.cleanTitle(keyword);
      }
    }

    for (const line of lines.slice(0, 150)) {
      if (
        this.looksLikeContactOrMeta(line) ||
        this.isBadKeywordLine(line) ||
        this.isSectionHeading(line) ||
        this.looksLikeEducationDegree(line)
      ) {
        continue;
      }

      const keyword = this.findStrongestJobKeyword(line);

      if (keyword && this.isValidTitle(keyword)) {
        return this.cleanTitle(keyword);
      }
    }

    return '';
  }

  private removeDuplicateTitleCandidates(
    candidates: Array<{ title: string; score: number; source: string; index: number }>
  ): Array<{ title: string; score: number; source: string; index: number }> {
    const map = new Map<string, { title: string; score: number; source: string; index: number }>();

    candidates.forEach(candidate => {
      const key = this.normalizeText(candidate.title);
      const existing = map.get(key);

      if (!existing || candidate.score > existing.score) {
        map.set(key, candidate);
      }
    });

    return Array.from(map.values());
  }

  private normalizeText(value: string): string {
    return String(value || '')
      .toLowerCase()
      .replace(/[ًٌٍَُِّْـ]/g, '')
      .replace(/[إأآا]/g, 'ا')
      .replace(/ى/g, 'ي')
      .replace(/ة/g, 'ه')
      .replace(/\s+/g, ' ')
      .trim();
  }

  private escapeRegex(value: string): string {
    return String(value).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }

  private countWords(value: string): number {
    return value.match(/[A-Za-z\u0600-\u06FF]+/g)?.length || 0;
  }

  private startProgress(): void {
    this.clearProgress();

    if (!this.isBrowser) {
      return;
    }

    this.progressInterval = window.setInterval(() => {
      if (this.progress < 90) {
        this.progress += 10;
      }
    }, 180);
  }

  private stopLoading(): void {
    this.clearProgress();
    this.isUploading = false;

    if (this.progress !== 100) {
      this.progress = 0;
    }
  }

  private clearProgress(): void {
    if (this.progressInterval && this.isBrowser) {
      window.clearInterval(this.progressInterval);
    }

    this.progressInterval = null;
  }
}
